import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const SocketContext = createContext(null);


export function SocketProvider({ children }) {
  const { authenticated, loading } = useAuth();

  const socket = useRef(null);

  const [status, setStatus] = useState({ state: "idle", uniqueId: "" });
  const [liveUser, setLiveUser] = useState(() => localStorage.getItem("liveUser") || "");

  useEffect(() => {
    if (loading || !authenticated) {
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      }

      setStatus({ state: "idle", uniqueId: "", error: "" });
      return;
    }

    const SOCKET_URL = "";

    socket.current = io(SOCKET_URL, {
      path: "/socket.io",
      transports: ["polling", "websocket"],
      withCredentials: true,
    });

    socket.current.on("connect", () => {
      console.log("[SOCKET] conectado:", socket.current.id);

      const saved = localStorage.getItem("liveUser");
      if (saved) {
        socket.current.emit("join", { uniqueId: saved });
      }
    });

    socket.current.on("reconnect", () => {
      const saved = localStorage.getItem("liveUser");
      if (saved) {
        socket.current.emit("join", { uniqueId: saved });
      }
    });

    socket.current.on("status", (st) => {
      setStatus(st);
    });

    socket.current.on("connect_error", (err) => {
      console.log("[SOCKET] connect_error:", err?.message || err);
      setStatus((prev) => ({
        ...prev,
        state: "error",
        error: String(err?.message || err),
      }));
    });

    return () => {
      socket.current?.disconnect();
      socket.current = null;
    };
  }, [authenticated, loading]);

  const setLiveUserAndPersist = (u) => {
    const clean = String(u || "").trim();
    setLiveUser(clean);

    if (clean) localStorage.setItem("liveUser", clean);
    else localStorage.removeItem("liveUser");
  };

  const joinLive = (uniqueId) => {
    const clean = String(uniqueId || "").trim();
    setLiveUserAndPersist(clean);
    setStatus({ state: "connecting", uniqueId: clean });

    if (!socket.current) return;

    socket.current.emit("join", { uniqueId: clean });
  };

  const clearLive = () => {
    setLiveUserAndPersist("");
    setStatus({ state: "idle", uniqueId: "", error: "" });
  };

  const startSale = ({ price, qty, durationSeconds, articulo } = {}) => {
    if (!socket.current) return;
    socket.current.emit("venta:start", {
      uniqueId: liveUser,
      price: price != null ? Number(price) : null,
      qty: qty != null ? Number(qty) : 1,
      durationSeconds: durationSeconds != null ? Number(durationSeconds) : 30,
      articulo: articulo || null,
    });
  };

  const resetSale = () => {
    if (!socket.current) return;
    socket.current.emit("venta:reset", { uniqueId: liveUser });
  };

  const startAuction = ({ durationSeconds = 30, minBid = 0, articulo = null } = {}) => {
    if (!socket.current) return;
    socket.current.emit("subasta:start", {
      uniqueId: liveUser,
      durationSeconds: Number(durationSeconds) || 30,
      minBid: Number(minBid) || 0,
      articulo: articulo || null,
    });
  };

  const mostrarProducto = (producto) => {
    if (!socket.current) return;
    socket.current.emit("producto:mostrar", producto);
  };

  const ocultarProducto = () => {
    if (!socket.current) return;
    socket.current.emit("producto:ocultar");
  };

  const resetAuction = () => {
    if (!socket.current) return;
    socket.current.emit("subasta:reset", { uniqueId: liveUser });
  };

  const value = useMemo(
    () => ({
      socket,
      status,
      liveUser,
      setLiveUser: setLiveUserAndPersist,
      joinLive,
      clearLive,
      startSale,
      resetSale,
      startAuction,
      resetAuction,
      mostrarProducto,
      ocultarProducto,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, liveUser]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocket debe usarse dentro de SocketProvider");
  return ctx;
}